import { initMapsInActiveTab } from '../map/googleMap';

class Tab {
  private buttonSelector = '.js-tab-button';

  private activeClass = 'isActive';
  private container: HTMLElement;
  private tabButtons: NodeListOf<HTMLElement>;
  private tabContent: NodeListOf<HTMLElement>;

  constructor(container: string | HTMLElement) {
    if (typeof container === 'string') {
      const el = document.querySelector<HTMLElement>(container);
      if (!el) return;
      this.container = el;
    } else {
      this.container = container;
    }

    this.init();
  }

  private init(): void {
    this.setupButtons();
    this.setupContent();
    this.checkforUrlHash();
  }

  private setupButtons(): void {
    this.tabButtons = this.container.querySelectorAll(this.buttonSelector);

    this.tabButtons.forEach(btn => {
      btn.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  private setupContent(): void {
    const contentSelector: string = Array.prototype.slice
      .call(this.tabButtons)
      .map((btn: HTMLElement) => `[data-tab-id="${btn.dataset.tab}"]`)
      .join(',');

    this.tabContent = document.querySelectorAll(contentSelector);
  }

  private onButtonClick(e: Event): void {
    e.preventDefault();
    const btn = (e.target as HTMLElement).closest(
      this.buttonSelector
    ) as HTMLElement | null;
    if (!btn?.dataset.tab) return;
    this.changeActiveTab(btn.dataset.tab);
  }

  changeActiveTab(id: string): void {
    this.tabButtons.forEach(btn => {
      btn.classList.remove(this.activeClass);

      if (id === btn.dataset.tab) {
        btn.classList.add(this.activeClass);
      }
    });

    this.tabContent.forEach(tab => {
      tab.classList.remove(this.activeClass);

      if (id === tab.dataset.tabId) {
        tab.classList.add(this.activeClass);
      }
    });

    this.updateHashLocation(id);
    this.updateSwipersInActiveTab();
    this.updateMapsInActiveTab();
  }

  /**
   * Swipers inside hidden tabs (display:none) init with zero width.
   * After the tab becomes visible, Swiper must recalculate layout.
   */
  private updateSwipersInActiveTab(): void {
    const activeTab = document.querySelector('.tab__content.isActive');
    if (!activeTab) return;

    const runUpdate = (): void => {
      activeTab.querySelectorAll('.js-swiper').forEach(el => {
        const swiperInstance = (el as HTMLElement & {
          swiper?: { update: () => void };
        }).swiper;
        if (swiperInstance?.update) {
          swiperInstance.update();
        }
      });
    };

    window.requestAnimationFrame(() => {
      runUpdate();
      window.requestAnimationFrame(runUpdate);
    });
  }

  /**
   * Google Maps in hidden tabs need init after the pane is visible (same as Swiper).
   */
  private updateMapsInActiveTab(): void {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        initMapsInActiveTab();
      });
    });
  }

  private updateHashLocation(hash: string): void {
    window.history.pushState(null, '', `#${hash}`);
  }

  private checkforUrlHash(): void {
    const { hash } = window.location;

    if (hash) {
      this.changeActiveTab(hash.substring(1));
    }
  }
}

export default Tab;
